import React, { useEffect, useState } from 'react'
import { useParams, generatePath, useNavigate } from 'react-router-dom'
import api from 'helpers/api'
import { IOrganisationTeamDetail, ISitesModalOrganisation } from 'types'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import Button from 'components/button'
import Skeleton from 'components/skeleton'
import Select from 'components/select'
import { People, Users, Organisation as OrganisationIcon } from 'components/icons'
import { APP_URL, PROJECT } from 'app-constants'
import { TEAM_SEATS, TEAM_DASHBOARD, PROFILE } from 'routes'
import { capitaliseFirstLetter, validateSourceUrl, exportToCsv } from 'helpers/utils'
import { formatShortDate } from 'helpers/date'
import StatsCard from 'components/statsCard'
import DataPanel from 'components/dataPanel'
import OrganisationSitesModal from 'views/organisation/components/sites'

const TeamDashboard: React.FC = () => {
  const params: { teamId?: string } = useParams()
  const navigate = useNavigate()
  const { displayErrorMessage } = useApplicationStore()
  const { user } = useAuth()

  const [loading, setLoading] = useState<boolean>(true)
  const [teamDetail, setTeamDetail] = useState<IOrganisationTeamDetail>()
  const [organisation, setOrganisation] = useState<ISitesModalOrganisation>()
  const [sitesModalOpen, setSitesModalOpen] = useState<boolean>(false)
  const [userSearch, setUserSearch] = useState<string>('')
  const [hubSearch, setHubSearch] = useState<string>('')

  const fetchTeam = () => {
    if (!params.teamId) return
    setLoading(true)
    api
      .getTeamSeats(params.teamId)
      .then((response) => setTeamDetail(response.data))
      .catch(displayErrorMessage)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    document.title = 'Team Dashboard'
  }, [])

  useEffect(() => {
    fetchTeam()
  }, [params.teamId])

  useEffect(() => {
    const orgId = user?.team?.orgPublicId
    if (!orgId) return
    api
      .getOrganisationSites(orgId)
      .then((response) => {
        setOrganisation({
          publicId: orgId,
          sites: response.data,
          users: [],
          teams: []
        })
      })
      .catch(() => {})
  }, [user?.team?.orgPublicId])

  // This page only loads the organisation's sites, so the sites modal is given this team's members to pick
  // site admins from. External members are left out because site admins must belong to the organisation.
  const siteAdminCandidates = (teamDetail?.members ?? [])
    .filter((m) => !m.isExternal)
    .map((m) => ({ publicId: m.email, name: m.name || m.email, email: m.email, createdAt: m.createdAt }))

  const managedTeams = user?.managedTeams ?? []
  const hasMultipleTeams = managedTeams.length > 1

  const computeFilteredMembers = () => {
    if (!teamDetail) return []
    if (!userSearch) return teamDetail.members
    const search = userSearch.toLowerCase()
    return teamDetail.members.filter(
      (m) => m.name.toLowerCase().includes(search) || m.email.toLowerCase().includes(search)
    )
  }
  const filteredMembers = computeFilteredMembers()

  const computeFilteredHubs = () => {
    if (!teamDetail) return []
    if (!hubSearch) return teamDetail.hubs
    const search = hubSearch.toLowerCase()
    return teamDetail.hubs.filter(
      (h) => h.name?.toLowerCase().includes(search) || h.site?.name?.toLowerCase().includes(search)
    )
  }
  const filteredHubs = computeFilteredHubs()

  const buildUsersTableData = () => {
    const activeRows = filteredMembers.map((member) => ({
      profilePicture: {
        value: member.email,
        label: (
          <img
            src={validateSourceUrl(member.profilePicture ?? undefined)}
            className="object-cover rounded-full mr-2.5 w-10 h-10"
            alt={member.name || 'Anonymous'}
          />
        )
      },
      name: {
        label: (
          <span className="flex items-center gap-2">
            {member.name}
            {member.isExternal && (
              <span className="text-xs text-blue bg-really-light-blue px-2 py-0.5 rounded-full font-bold">
                External
              </span>
            )}
          </span>
        ),
        value: member.name
      },
      email: { label: member.email, value: member.email },
      profile: {
        value: member.publicId,
        label: member.firebaseUserId ? (
          <Button
            to={generatePath(PROFILE, { userId: member.firebaseUserId, tab: 'information' })}
            internalType="outline"
          >
            View Profile
          </Button>
        ) : null
      }
    }))
    const pendingRows = (teamDetail?.pendingMembers ?? []).map((p) => ({
      profilePicture: { value: p.email, label: <div className="w-10 h-10 rounded-full bg-surface-strong mr-2.5" /> },
      name: {
        label: (
          <span className="flex items-center gap-2">
            {p.email}
            <span className="text-xs text-orange bg-really-light-yellow px-2 py-0.5 rounded-full font-bold">
              Invited
            </span>
          </span>
        ),
        value: p.email
      },
      email: { label: p.email, value: p.email },
      profile: { value: '', label: null }
    }))
    return [...activeRows, ...pendingRows]
  }

  const usersTableData = buildUsersTableData()

  const hubsTableData = filteredHubs.map((hub) => ({
    name: { value: hub.name, label: hub.name },
    createdAt: {
      value: hub.createdAt,
      label: formatShortDate(hub.createdAt)
    },
    site: {
      value: hub.site ? hub.site.name : 'No site mapped',
      label: hub.site ? hub.site.name : 'No site mapped'
    },
    isPublic: { value: hub.public ? 'Yes' : 'No', label: hub.public ? 'Yes' : 'No' },
    access: {
      value: hub.publicId,
      label: (
        <a href={`${APP_URL}/hub/${hub.publicId}`} target="_blank" rel="noopener noreferrer">
          <Button internalType="outline">Go to {capitaliseFirstLetter(PROJECT)}</Button>
        </a>
      )
    }
  }))

  const handleExportUsers = () => {
    if (!teamDetail) return
    const headers = ['Name', 'Email']
    const rows = filteredMembers.map((m) => [m.name || '', m.email || ''])
    exportToCsv(`${teamDetail.name} - Members.csv`, headers, rows)
  }

  const handleExportHubs = () => {
    if (!teamDetail) return
    const headers = ['Name', 'Created At', 'Site', 'Is Public']
    const rows = filteredHubs.map((h) => [
      h.name,
      new Date(h.createdAt).toLocaleDateString(),
      h.site ? h.site.name : 'No site mapped',
      h.public ? 'Yes' : 'No'
    ])
    exportToCsv(`${teamDetail.name} - ${capitaliseFirstLetter(PROJECT)}s.csv`, headers, rows)
  }

  return (
    <div className="w-full p-5 flex flex-col gap-4 h-screen min-h-0">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        {hasMultipleTeams ? (
          <div className="flex items-center gap-2 mr-auto">
            <People className="h-5 w-5" />
            <Select
              className="min-w-[200px]"
              options={managedTeams.map((t) => ({ label: t.name, value: t.publicId }))}
              optionsSelected={params.teamId ? [params.teamId] : []}
              onOptionClick={(teamId: string) => {
                navigate(generatePath(TEAM_DASHBOARD, { teamId }))
              }}
            />
          </div>
        ) : (
          <h1 className="mr-auto flex items-center gap-2">
            <People className="h-5 w-5" />
            {teamDetail?.name ?? 'Team Dashboard'}
          </h1>
        )}
      </div>

      {/* Stats cards */}
      {loading ? (
        <div className="grid grid-cols-4 max-lg:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[100px]" />
          ))}
        </div>
      ) : (
        teamDetail && (
          <div
            className={`grid gap-4 ${user?.team?.orgPlanTier === 'professional' ? 'grid-cols-2 max-lg:grid-cols-1' : 'grid-cols-3 max-lg:grid-cols-2'}`}
          >
            <StatsCard
              label="Paid User Seats"
              icon={<Users className="h-4 w-4" />}
              value={`${teamDetail.seatsUsed} / ${teamDetail.seatLimit}`}
              sub={`${teamDetail.seatsAvailable} available`}
              footer={
                <Button
                  internalType="outline"
                  to={params.teamId ? generatePath(TEAM_SEATS, { teamId: params.teamId }) : '#'}
                >
                  Manage Seats
                </Button>
              }
            />

            {user?.team?.orgPlanTier !== 'professional' && (
              <StatsCard
                label="Sites"
                icon={<OrganisationIcon className="h-4 w-4" />}
                value={teamDetail.siteCount}
                sub="Sites tagged to this team"
                footer={
                  <Button internalType="outline" onClick={() => setSitesModalOpen(true)} disabled={!organisation}>
                    Manage Sites
                  </Button>
                }
              />
            )}

            <StatsCard label="Managers" icon={<People className="h-4 w-4" />}>
              <div className="mt-2">
                {teamDetail.managers.map((m) => (
                  <div key={m.publicId} className="text-sm">
                    {m.name}
                  </div>
                ))}
              </div>
            </StatsCard>
          </div>
        )
      )}

      {/* Users and Hubs tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 flex-1 min-h-0 overflow-hidden">
        <DataPanel
          title={`${teamDetail?.members.length ?? 0} Team Members`}
          items={teamDetail?.members ?? []}
          getDate={(m) => m.createdAt}
          chartLabel="Cumulative users"
          tableData={usersTableData}
          tableColumns={[
            { header: '', id: 'profilePicture' },
            { header: 'Name', id: 'name' },
            { header: 'Email', id: 'email' },
            { header: 'Profile', id: 'profile' }
          ]}
          searchPlaceholder="Search for a user..."
          search={userSearch}
          onSearchChange={setUserSearch}
          onExport={handleExportUsers}
          loading={loading}
          fullscreen
        />

        <DataPanel
          title={`${teamDetail?.hubs.length ?? 0} ${capitaliseFirstLetter(PROJECT)}s (Associated with sites)`}
          items={teamDetail?.hubs ?? []}
          getDate={(h) => h.createdAt}
          chartLabel={`Cumulative ${capitaliseFirstLetter(PROJECT)}s`}
          tableData={hubsTableData}
          tableColumns={[
            { header: 'Name', id: 'name' },
            { header: 'Created At', id: 'createdAt' },
            { header: 'Site', id: 'site' },
            { header: 'Public?', id: 'isPublic' },
            { header: '', id: 'access' }
          ]}
          searchPlaceholder={`Search for a ${PROJECT}...`}
          search={hubSearch}
          onSearchChange={setHubSearch}
          onExport={handleExportHubs}
          loading={loading}
          fullscreen
        />
      </div>

      {sitesModalOpen && organisation && (
        <OrganisationSitesModal
          open={sitesModalOpen}
          onClose={() => setSitesModalOpen(false)}
          organisation={teamDetail ? { ...organisation, teams: [teamDetail] } : organisation}
          onChanged={(updatedOrg) => {
            setOrganisation(updatedOrg)
            if (teamDetail && params.teamId) {
              const newCount = updatedOrg.sites.filter((s) => s.teamId === params.teamId).length
              if (newCount !== teamDetail.siteCount) {
                setTeamDetail({ ...teamDetail, siteCount: newCount })
              }
            }
          }}
          filterTeamId={params.teamId}
          lockTeam
          adminCandidates={siteAdminCandidates}
        />
      )}
    </div>
  )
}

export default TeamDashboard
